// src/api/core.js
// Import impératif du store Zustand (sans hook, safe hors composant React)
import { useStore } from '../store/index.js'

const TOKEN_KEY = 'token'
const CURRENT_USER_KEY = 'currentUser'
let memoryToken = null
const CSRF_COOKIE_NAME = 'XSRF-TOKEN'

// Nettoyage legacy: on ne stocke plus le JWT en localStorage
try {
  localStorage.removeItem(TOKEN_KEY)
} catch {}

/* ================================
 * ✅ API base (fix proxy/port)
 * - CRA: REACT_APP_API_BASE=http://localhost:4000
 * - Vite: VITE_API_BASE=http://localhost:4000
 * ================================ */
const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) ||
  ''

function buildUrl(url) {
  if (!url) return url
  if (url.startsWith('http://') || url.startsWith('https://')) return url

  const base = String(API_BASE || '').replace(/\/$/, '')
  if (!base) return url // si proxy dev, OK

  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`
}

/* ================================
 * Utils JWT
 * ================================ */
function isJwtExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 < Date.now()
  } catch {
    return true
  }
}

/* ================================
 * Token helpers
 * ================================ */
export function getToken() {
  try {
    const token = memoryToken
    if (!token || token === 'undefined' || token === 'null') return null
    if (isJwtExpired(token)) {
      memoryToken = null
      return null
    }
    return token
  } catch {
    return null
  }
}

export function setToken(token) {
  try {
    if (!token) memoryToken = null
    else memoryToken = token
  } catch {}
}

export function clearToken() {
  try {
    memoryToken = null
  } catch {}
}

/* ================================
 * Current user helpers
 * ================================ */
export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(CURRENT_USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function setCurrentUser(user) {
  try {
    if (user == null) localStorage.removeItem(CURRENT_USER_KEY)
    else localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user))
  } catch {}
}

export function clearCurrentUser() {
  try {
    localStorage.removeItem(CURRENT_USER_KEY)
  } catch {}
}

function getCookie(name) {
  try {
    const parts = String(document.cookie || '').split(';')
    for (const part of parts) {
      const idx = part.indexOf('=')
      if (idx === -1) continue
      const key = part.slice(0, idx).trim()
      if (key !== name) continue
      return decodeURIComponent(part.slice(idx + 1).trim())
    }
  } catch {}
  return null
}

/* ================================
 * Headers helpers
 * ================================ */
export function authHeaders(extra = {}) {
  const headers = { ...extra }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const csrf = getCookie(CSRF_COOKIE_NAME)
  if (csrf) headers['X-CSRF-Token'] = csrf
  return headers
}

/* ================================
 * Response helpers
 * ================================ */
function isJsonResponse(res) {
  const ct = res.headers.get('content-type') || ''
  return ct.includes('application/json')
}

function looksLikeJsonString(s) {
  if (typeof s !== 'string') return false
  const t = s.trim()
  return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))
}

function isFormData(v) {
  return typeof FormData !== 'undefined' && v instanceof FormData
}

async function readErrorPayload(response) {
  try {
    if (isJsonResponse(response)) return await response.json()

    const txt = await response.text()
    if (looksLikeJsonString(txt)) {
      try {
        return JSON.parse(txt)
      } catch {
        return txt
      }
    }
    return txt
  } catch {
    return `HTTP ${response.status}`
  }
}

function makeHttpError(status, payload) {
  let message = `HTTP ${status}`

  if (payload && typeof payload === 'object') {
    message = payload.error || payload.message || payload.hint || message
  } else if (typeof payload === 'string' && payload.trim()) {
    message = payload.trim()
  }

  const err = new Error(message)
  err.status = status
  err.data = payload
  return err
}

/* ================================
 * Offline queue helpers
 * ================================ */
const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE']

function getActionLabel(url, method) {
  const path = String(url || '').replace(/\?.*$/, '')
  if (path.includes('/orders')) return method === 'DELETE' ? 'Suppression commande' : 'Commande'
  if (path.includes('/payments') || path.includes('/pay')) return 'Paiement'
  if (path.includes('/prestations'))
    return method === 'DELETE' ? 'Suppression prestation' : 'Prestation'
  if (path.includes('/customers')) return method === 'DELETE' ? 'Suppression client' : 'Client'
  if (path.includes('/products')) return method === 'DELETE' ? 'Suppression produit' : 'Produit'
  if (path.includes('/projects')) return method === 'DELETE' ? 'Suppression projet' : 'Projet'
  if (path.includes('/relances') || path.includes('/campaigns')) return 'Relance'
  if (path.includes('/expenses')) return 'Dépense'
  if (path.includes('/quotes')) return method === 'DELETE' ? 'Suppression devis' : 'Devis'
  return `${method} ${path}`
}

/* ================================
 * Auth 401 handler
 * ================================ */
function handle401() {
  clearToken()
  clearCurrentUser()

  if (!window.location.pathname.startsWith('/auth')) {
    window.location.replace('/auth')
  }
}

/* ================================
 * Fetch JSON (ou text)
 * - Supporte options.json (recommandé)
 * - Supporte options.body (string, object, FormData)
 * ================================ */
export async function apiFetch(url, options = {}) {
  let { body, json, headers = {}, ...rest } = options

  if (json !== undefined) {
    body = JSON.stringify(json)
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }

  if (body && typeof body === 'object' && !isFormData(body)) {
    body = JSON.stringify(body)
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }

  if (typeof body === 'string' && looksLikeJsonString(body)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }

  if (isFormData(body)) {
    delete headers['Content-Type']
  }

  const finalUrl = buildUrl(url)
  const method = (rest.method || 'GET').toUpperCase()

  // ── Interception offline : mettre les mutations en queue ─────────────────
  if (!navigator.onLine && WRITE_METHODS.includes(method)) {
    let bodyPayload = body
    try {
      if (typeof body === 'string') bodyPayload = JSON.parse(body)
    } catch {}
    const label = getActionLabel(url, method)
    useStore.getState().addToOfflineQueue({ url, method, body: bodyPayload, label })
    // Tenter le Background Sync si supporté
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((sw) => {
          if ('sync' in sw) sw.sync.register('fedis-offline-sync')
        })
        .catch(() => {})
    }
    const err = new Error(`${label} mis en queue — sera synchronisé à la reconnexion`)
    err.code = 'OFFLINE_QUEUED'
    err.queued = true
    throw err
  }
  // ─────────────────────────────────────────────────────────────────────────

  const response = await fetch(finalUrl, {
    credentials: rest.credentials || 'include',
    ...rest,
    body,
    headers: authHeaders(headers),
  })

  if (response.status === 401) {
    handle401()
    throw makeHttpError(401, { error: 'Unauthorized' })
  }

  if (!response.ok) {
    const payload = await readErrorPayload(response)
    throw makeHttpError(response.status, payload)
  }

  if (response.status === 204) return null
  if (isJsonResponse(response)) return response.json()
  return response.text()
}

/* ================================
 * Fetch Blob (PDF / Excel)
 * - Supporte options.json aussi
 * ================================ */
export async function apiFetchBlob(url, options = {}) {
  let { body, json, headers = {}, ...rest } = options

  if (json !== undefined) {
    body = JSON.stringify(json)
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }

  if (body && typeof body === 'object' && !isFormData(body)) {
    body = JSON.stringify(body)
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }

  if (isFormData(body)) {
    delete headers['Content-Type']
  }

  const finalUrl = buildUrl(url)

  const response = await fetch(finalUrl, {
    credentials: rest.credentials || 'include',
    ...rest,
    body,
    headers: authHeaders(headers),
  })

  if (response.status === 401) {
    handle401()
    throw makeHttpError(401, { error: 'Unauthorized' })
  }

  if (!response.ok) {
    const payload = await readErrorPayload(response)
    throw makeHttpError(response.status, payload)
  }

  return response.blob()
}

/* ================================
 * Download helper
 * ================================ */
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Delay revoke to avoid truncated/corrupted downloads on some browsers.
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url)
  }, 60_000)
}
